import Visibility from "@mui/icons-material/Visibility";
import ModalBox from "components/Modal";
import CurrencyIcon from "components/shared/CurrencyIcon";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import MessageBlock from "components/shared/MessageBlock";
import IbanModal from "components/shared/PaymentExport/IbanModal";
import StyledButton from "components/shared/StyledButton";
import { useTracker } from "contexts/tracker";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { formatNumber, maskString, replaceComma, toIntegerPrice } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { updateAdvancePayments } from "store/advancePaymentSlice";
import { updateBeneficiariesPaymentStatus } from "store/managementStore";
import type { IPaymentResource } from "store/paymentSlice";
import { addToPayment, EPaymentResourceType, EPaymentType } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import { EFetchStatus, PaymentStatus } from "store/types";
import type { Binary } from "types";

import variables from "../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";

export interface INewPaymentData {
    /**
     * Id of the resource to be attached to payment.
     */
    id: number;
    /**
     * Amount to be paid, full or not.
     */
    amount: number | null;
    /**
     * Name to display on table if several lines are selected.
     */
    name?: string;
    advancePaymentId?: number;
    formAmount?: number;
    /**
     * They have already paid part of it.
     */
    completingPayment?: boolean;
    bankInfoValid?: boolean;
    beneficiaryId?: number;
    iban?: string;
    /**
     * Required from management beneficiary table.
     */
    underTheMinimum?: Binary;
}

interface IBeneficiaryPaymentModal {
    /**
     * Type of payment: beneficiary, partial, advance...
     */
    type: EPaymentResourceType;
    /**
     * Data of payments to be created. Either one payment or several.
     * Payments can only be of same type (advance or beneficiary).
     * There cannot be more than on partial payment.
     */
    elementsData: INewPaymentData[];
    /**
     * Compulsory for beneficiary or partialPayment type.
     */
    periodId?: number;
    handleClose: () => void;
    /**
     * If they selected various lines.
     */
    hasSeveralPayments?: boolean;

    /**
     * Context of where the modal is opened from.
     */
    context?: string;
}

/**
 * Modal to create new payment (either full amount or partial) from Management-Beneficiary or from Advance page.
 */
const PaymentModal = ({
    elementsData,
    periodId,
    type,
    handleClose,
    context,
    hasSeveralPayments = false,
}: IBeneficiaryPaymentModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const tracker = useTracker();

    const { periodsDateRange } = useAppSelector((state) => state.period);
    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);
    const { internationalBankingEnabled } = useCompanySettings();

    const beneficiariesWithBankError: number = hasSeveralPayments
        ? elementsData.filter((item) => !item.bankInfoValid).length
        : 0;

    const [fetchStatus, setFetchStatus] = useState<TFetchStatus>(EFetchStatus.IDLE);
    const [wrongBankInfos, setWrongBankInfos] = useState(!hasSeveralPayments && !elementsData[0].bankInfoValid);
    const [benefWithBankErrors, setBenefWithBankErrors] = useState<number>(beneficiariesWithBankError);

    const [modalType, setModalType] = useState("");
    const [beneficiary, setBeneficiary] = useState<INewPaymentData>();

    const totalDueOnPeriod = (elementsData[0].amount ?? 0) / 100;

    const defaultValues = {
        amount: hasSeveralPayments ? null : type === EPaymentResourceType.PARTIAL_PAYMENT ? null : totalDueOnPeriod,
        paymentDate: null,
        isBulkPayment: null,
    };

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({ defaultValues, mode: "all" });

    const addToBulkPaymentOptions = [
        { label: t("PartialPayment.PaymentModal.SetPaid") },
        { label: t("PartialPayment.PaymentModal.AddTransfer") },
    ];

    const paymentTypeOption = watch("isBulkPayment");

    /**
     * If they are adding the payment to TO_PAY.
     */
    const isBulkPayment = paymentTypeOption ? !!+paymentTypeOption : undefined;

    const enteredAmount = watch("amount");
    const date = watch("paymentDate");

    const amountHigherThanTotal = hasSeveralPayments
        ? false
        : replaceComma(enteredAmount?.toString() ?? "0") > totalDueOnPeriod;
    const amountDifferentFromDefault = enteredAmount !== totalDueOnPeriod;

    const setResourceType = (completingPayment: boolean | undefined) => {
        let _type = type;
        const amountDifferentFromDefault = enteredAmount !== totalDueOnPeriod;
        if ((!hasSeveralPayments && amountDifferentFromDefault) || completingPayment) {
            _type = EPaymentResourceType.PARTIAL_PAYMENT;
        }

        return _type;
    };

    const onSubmit = async (form) => {
        if (tracker && context) {
            tracker.trackEvent("Button clicked", {
                button: context,
            });
        }

        if (elementsData.length < 1) return;
        setFetchStatus(EFetchStatus.PENDING);

        let dataPeriodId: number | undefined = undefined;
        if ([EPaymentResourceType.BENEFICIARY, EPaymentResourceType.PARTIAL_PAYMENT].includes(type)) {
            dataPeriodId = periodId;
        }
        if (isBulkPayment) {
            form.paymentDate = null;
        } else {
            form.paymentDate = form.paymentDate.toLocaleDateString("en-CA");
        }

        const resources: IPaymentResource[] = elementsData
            .filter((item) => (hasSeveralPayments && isBulkPayment ? item.bankInfoValid : true))
            .map((item) => ({
                type: setResourceType(item.completingPayment),
                id: item.id,
                amount: hasSeveralPayments ? item.amount ?? 0 : toIntegerPrice(form.amount),
                periodId: dataPeriodId,
                underTheMinimum: item.underTheMinimum,
            }));

        const data = {
            method: isBulkPayment ? EPaymentType.GROUPED : EPaymentType.MANUAL,
            paymentDate: form.paymentDate,
            resources,
        };

        try {
            const response = await dispatch(addToPayment({ data }));

            if (response.meta.requestStatus === EFetchStatus.FULFILLED) {
                // If not bulk payment, means it's payed.
                // If it is partial, we set as Not Paid.
                // If bulk, then set as To_Pay
                const status = !isBulkPayment
                    ? PaymentStatus.PAID
                    : amountDifferentFromDefault
                      ? PaymentStatus.NOT_PAID
                      : PaymentStatus.TO_PAY;

                if (type === EPaymentResourceType.ADVANCE_PAYMENT) {
                    dispatch(updateAdvancePayments({ elements: elementsData, status, paymentDate: data.paymentDate }));
                } else {
                    dispatch(
                        updateBeneficiariesPaymentStatus({
                            elements: resources,
                            status,
                            formAmount: +(form.amount ?? 0) * 100,
                        }),
                    );
                }
                updatePaymentPageState();
                setFetchStatus(EFetchStatus.FULFILLED);
            }
        } catch (err) {
            setFetchStatus(EFetchStatus.REJECTED);
        } finally {
            handleClose();
        }
    };

    const handleIban = (e, payment: INewPaymentData) => {
        e.stopPropagation();
        setBeneficiary(payment);
        setModalType("iban");
    };

    const renderIban = (value: string, payment: INewPaymentData) => {
        return (
            <div>
                {payment?.bankInfoValid ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <span>{maskString(value)}</span>
                        <Visibility
                            sx={{ height: "15px", color: variables["foreground"], cursor: "pointer" }}
                            onClick={(e) => handleIban(e, payment)}
                        />
                    </div>
                ) : !payment.bankInfoValid ? (
                    <button className="payment-iban-btn" onClick={(e) => handleIban(e, payment)} type="button">
                        {t("PaymentModal.IbanButtonFix")}
                    </button>
                ) : (
                    <button className="payment-iban-btn" onClick={(e) => handleIban(e, payment)} type="button">
                        {t("PaymentModal.IbanButtonEntry")}
                    </button>
                )}
            </div>
        );
    };

    const paymentColumns: LastElementTableColumns[] = [
        {
            id: "name",
            label:
                type === EPaymentResourceType.ADVANCE_PAYMENT
                    ? t("History.Type.advancePayments")
                    : t("PaymentModal.Beneficiary"),
            avatar: type === EPaymentResourceType.ADVANCE_PAYMENT ? false : true,
            sort: false,
            align: "left",
            avatarType: "circular",
            maxWidth: "200px",
        },
        {
            id: "iban",
            label: t("PaymentModal.Iban"),
            avatar: false,
            align: "left",
            sort: false,
            format: (value: any, _, payment) => {
                return renderIban(value, payment);
            },
        },
        {
            id: "amount",
            label: t("PaymentModal.NetValueOwed"),
            avatar: false,
            align: "right",
            sort: false,
            format: (value: any) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
    ];

    const bottomFooterContent = (
        <div className="info-footer">
            <div className="info-footer-text">
                <span className="red">
                    {benefWithBankErrors !== 0
                        ? `${benefWithBankErrors}/${elementsData.length} ${t("PaymentModal.IbanIssuesText")}`
                        : ""}
                </span>
            </div>
        </div>
    );

    const handleConfirmBankInfos = (beneficiaryId: number, iban: string) => {
        if (hasSeveralPayments) {
            const currentPayment = elementsData.find(
                (item) => item.beneficiaryId === beneficiaryId || item.id === beneficiaryId,
            );
            if (currentPayment) {
                currentPayment.bankInfoValid = true;
                currentPayment.iban = iban;
                setBenefWithBankErrors((prev) => prev - 1);
            }
        } else {
            setWrongBankInfos(false);
        }
    };

    /**
     * Conditions to disable confirm btn. Written like this for clarity.
     */
    const disableConfirmButton = () => {
        const disable = false;

        // When request is pending:
        if (fetchStatus === EFetchStatus.PENDING) {
            return true;
        }

        // When they haven't selected a payment option yet:
        if (!watch("isBulkPayment")) {
            return true;
        }

        if (isBulkPayment && hasSeveralPayments && benefWithBankErrors === elementsData.length) {
            return true;
        }

        // When they choose to pay directly but haven't selected a date:
        if (!isBulkPayment && !date) {
            return true;
        }

        // When they haven't selected various benef, they choose a transfer and bank infos are wrong.
        if (!hasSeveralPayments && isBulkPayment && wrongBankInfos) {
            return true;
        }

        return disable;
    };

    return (
        <form className={styles["payment-modal-container"]} onSubmit={handleSubmit(onSubmit)}>
            <ModalBox
                open={modalType === "iban"}
                mainTitle={beneficiary?.name ?? ""}
                subTitle={t("PaymentModal.IbanModalSubtitle")}
                onClose={() => setModalType("")}
            >
                <IbanModal
                    beneficiaryId={beneficiary?.beneficiaryId ?? beneficiary?.id}
                    close={() => setModalType("")}
                    confirmBankInfos={handleConfirmBankInfos}
                />
            </ModalBox>
            {!hasSeveralPayments && (
                <div className={styles["amount-field"]}>
                    <NumberFormComponent
                        control={control}
                        controlName="amount"
                        label={t("PartialPayment.PaymentModal.Amount")}
                        icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                        float
                        disabled={type === EPaymentResourceType.ADVANCE_PAYMENT}
                        disabledText={t("PartialPayment.PaymentModal.AdvanceBlock")}
                        error={amountHigherThanTotal || errors.amount}
                        customErrorMessage={amountHigherThanTotal ? t("PartialPayment.PaymentModal.AmountError") : ""}
                    />
                    {type !== EPaymentResourceType.ADVANCE_PAYMENT && (
                        <span>
                            {t("PartialPayment.PaymentModal.BalanceLeft", {
                                amount: formatNumber(totalDueOnPeriod, { isCurrency: true }),
                            })}
                        </span>
                    )}
                </div>
            )}

            <div className={styles["bulk-payment"]}>
                <RadioFormComponent
                    label={t("PartialPayment.PaymentModal.RadioTitle")}
                    control={control}
                    controlName="isBulkPayment"
                    column
                    options={addToBulkPaymentOptions}
                />
            </div>

            {hasSeveralPayments && (
                <div className={styles["beneficiaries-info"]}>
                    <BodyHighlight color={"grey"} style={{ fontWeight: "500" }}>
                        {t("PartialPayment.ModalElementList", {
                            element:
                                type === EPaymentResourceType.ADVANCE_PAYMENT
                                    ? t("BPStatement.Advance")
                                    : t("NavigationBar.Beneficiars"),
                        })}
                    </BodyHighlight>
                    <LastElementTable
                        data={elementsData}
                        columns={paymentColumns}
                        max={9999}
                        maxHeight={300}
                        avatarSourceValue="beneficiaryAvatarUrl"
                        bottomFooterContent={bottomFooterContent}
                    />
                    {isBulkPayment && benefWithBankErrors > 0 && (
                        <MessageBlock
                            type="warning"
                            text={t("PartialPayment.PaymentModal.WrongIbanBulk", { count: benefWithBankErrors })}
                            action={() => {
                                const first = elementsData.find((item) => !item.bankInfoValid);
                                if (first) {
                                    setBeneficiary(first);
                                    setModalType("iban");
                                }
                            }}
                            actionText={t("PaymentModal.IbanButtonFix")}
                            style={{ marginTop: "1rem" }}
                        />
                    )}
                </div>
            )}

            {isBulkPayment &&
                !hasSeveralPayments &&
                wrongBankInfos &&
                (internationalBankingEnabled ? (
                    <MessageBlock type="error" text={t("PartialPayment.PaymentModal.WrongIban")} />
                ) : (
                    <MessageBlock
                        type="warning"
                        text={t("PartialPayment.PaymentModal.WrongIban")}
                        action={() => {
                            setBeneficiary(elementsData[0]);
                            setModalType("iban");
                        }}
                        actionText={t("PaymentModal.IbanButtonFix")}
                        style={{ marginTop: "1rem" }}
                    />
                ))}

            {isBulkPayment !== undefined && !isBulkPayment && (
                <Fragment>
                    <DateFormComponent
                        control={control}
                        controlName="paymentDate"
                        label={t("PartialPayment.PaymentModal.PaymentDateLabel")}
                        error={errors.paymentDate}
                        {...(type === EPaymentResourceType.ADVANCE_PAYMENT
                            ? { maxYearDistance: 2 }
                            : { maxDate: new Date() })}
                    />
                    <SmallBody color="grey">{t("PartialPayment.PaymentModal.PaymentDate")}</SmallBody>
                </Fragment>
            )}

            <div className={styles["form-buttons"]}>
                <StyledButton buttonType="outlined" onClick={() => handleClose()}>
                    {t("ModalBase.Cancel")}
                </StyledButton>
                <StyledButton
                    type="submit"
                    buttonType="primary"
                    disabled={disableConfirmButton()}
                    loading={fetchStatus === EFetchStatus.PENDING}
                >
                    {t("ModalBase.Confirm")}
                </StyledButton>
            </div>
        </form>
    );
};

export default PaymentModal;
